import { byteSize, MAX_ICON_BYTES } from "./limits";

/** Default relative path for the project indicator icon */
export const DEFAULT_ICON_FILE = "assets/icon.svg";

export { MAX_ICON_BYTES };
export const MAX_ICON_DIMENSION = 256;

const ICON_EXTENSIONS = new Set([".svg", ".png", ".webp"]);

export const DEFAULT_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="64" height="64">
  <rect width="64" height="64" rx="12" fill="#1565C0"/>
  <path d="M12 44 L24 28 L36 36 L52 18" stroke="#fff" stroke-width="4" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
  <circle cx="52" cy="18" r="3" fill="#26A69A"/>
</svg>`;

export interface IconValidationResult {
  ok: boolean;
  error?: string;
  mimeType?: string;
  decodedBytes?: number;
  width?: number;
  height?: number;
}

export function isIconFilePath(path: string): boolean {
  const ext = path.slice(path.lastIndexOf(".")).toLowerCase();
  return ICON_EXTENSIONS.has(ext);
}

export function iconExtension(path: string): string {
  return path.slice(path.lastIndexOf(".")).toLowerCase();
}

export function mimeTypeForIconPath(path: string): string {
  const ext = iconExtension(path);
  if (ext === ".svg") return "image/svg+xml";
  if (ext === ".png") return "image/png";
  if (ext === ".webp") return "image/webp";
  return "application/octet-stream";
}

function parseDataUrl(
  content: string
): { mimeType: string; base64: string } | null {
  const match = /^data:([^;,]+);base64,(.+)$/s.exec(content.trim());
  if (!match) return null;
  return { mimeType: match[1]!, base64: match[2]! };
}

function decodeBase64Size(base64: string): number {
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4) - padding;
}

/** Read PNG/WebP width/height from binary header (best-effort). */
export function readRasterDimensions(
  bytes: Uint8Array
): { width: number; height: number } | null {
  if (bytes.length >= 24 && bytes[0] === 0x89 && bytes[1] === 0x50) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return {
      width: view.getUint32(16, false),
      height: view.getUint32(20, false),
    };
  }
  if (
    bytes.length >= 30 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    return {
      width: view.getUint16(26, true) + 1,
      height: view.getUint16(28, true) + 1,
    };
  }
  return null;
}

export function bytesFromIconContent(
  path: string,
  content: string
): Uint8Array {
  const ext = iconExtension(path);
  if (ext === ".svg") {
    return new TextEncoder().encode(content);
  }
  const parsed = parseDataUrl(content);
  if (!parsed) {
    throw new Error("Raster icon must be stored as a data URL");
  }
  const binary = atob(parsed.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export function iconContentToDataUrl(path: string, content: string): string {
  const ext = iconExtension(path);
  if (ext === ".svg") {
    const encoded = encodeURIComponent(content.trim());
    return `data:image/svg+xml,${encoded}`;
  }
  const trimmed = content.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  throw new Error("Raster icon content must be a data URL");
}

export async function validateIconContent(
  path: string,
  content: string
): Promise<IconValidationResult> {
  if (!isIconFilePath(path)) {
    return { ok: false, error: "Icon must be .svg, .png, or .webp" };
  }

  const ext = iconExtension(path);

  try {
    if (ext === ".svg") {
      const trimmed = content.trim();
      if (!trimmed.includes("<svg")) {
        return { ok: false, error: "SVG icon must contain an <svg> element" };
      }
      const decodedBytes = byteSize(trimmed);
      if (decodedBytes > MAX_ICON_BYTES) {
        return {
          ok: false,
          error: `Icon exceeds ${formatKb(MAX_ICON_BYTES)} limit (${formatKb(decodedBytes)})`,
        };
      }
      return {
        ok: true,
        mimeType: "image/svg+xml",
        decodedBytes,
      };
    }

    const parsed = parseDataUrl(content);
    if (!parsed) {
      return {
        ok: false,
        error: "PNG/WebP icons must be uploaded as image files (data URL storage)",
      };
    }

    const expectedMime =
      ext === ".png" ? "image/png" : "image/webp";
    if (
      parsed.mimeType !== expectedMime &&
      parsed.mimeType !== `${expectedMime};base64`
    ) {
      return {
        ok: false,
        error: `Expected ${expectedMime} for ${ext} icon`,
      };
    }

    const decodedBytes = decodeBase64Size(parsed.base64);
    if (decodedBytes > MAX_ICON_BYTES) {
      return {
        ok: false,
        error: `Icon exceeds ${formatKb(MAX_ICON_BYTES)} limit (${formatKb(decodedBytes)})`,
      };
    }

    const bytes = bytesFromIconContent(path, content);
    const dims = readRasterDimensions(bytes);
    if (dims) {
      if (
        dims.width > MAX_ICON_DIMENSION ||
        dims.height > MAX_ICON_DIMENSION
      ) {
        return {
          ok: false,
          error: `Icon dimensions ${dims.width}×${dims.height} exceed ${MAX_ICON_DIMENSION}×${MAX_ICON_DIMENSION}`,
        };
      }
      return {
        ok: true,
        mimeType: expectedMime,
        decodedBytes,
        width: dims.width,
        height: dims.height,
      };
    }

    return {
      ok: true,
      mimeType: expectedMime,
      decodedBytes,
    };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : "Invalid icon file",
    };
  }
}

export async function readUploadAsIconContent(
  file: File
): Promise<{ path: string; content: string }> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".svg")) {
    const text = await file.text();
    const path = DEFAULT_ICON_FILE.endsWith(".svg")
      ? DEFAULT_ICON_FILE
      : `assets/${file.name}`;
    return { path, content: text };
  }
  if (name.endsWith(".png") || name.endsWith(".webp")) {
    const dataUrl = await readFileAsDataUrl(file);
    const ext = name.endsWith(".png") ? ".png" : ".webp";
    return { path: `assets/icon${ext}`, content: dataUrl };
  }
  throw new Error("Icon must be SVG, PNG, or WebP");
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") resolve(reader.result);
      else reject(new Error("Failed to read icon file"));
    };
    reader.onerror = () => reject(new Error("Failed to read icon file"));
    reader.readAsDataURL(file);
  });
}

function formatKb(bytes: number): string {
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}
